(() => {
  const API_BASE_URL = "https://luzione-ai-backend.onrender.com";
  const API_IMAGE_ENDPOINT = `${API_BASE_URL}/recommend-from-image`;
  const API_CHAT_ENDPOINT = `${API_BASE_URL}/chat`;
  const API_RESET_ENDPOINT = `${API_BASE_URL}/reset-memory`;
  const API_DESIGNER_LEADS_ENDPOINT = `${API_BASE_URL}/designer-leads`;

  const LUZIONE_WIDGET_VERSION = "2026-05-30-image-aware-concierge-v1";
  const FETCH_TIMEOUT_MS = 150000;
  const SHOP_DOMAIN = "luzione.myshopify.com";
  const PRODUCT_BASE_URL = "https://luzione.com/products/";
  const INPUT_PLACEHOLDER = "Ask about this room or describe what you want";

  console.info(`[Luzione AI] widget version ${LUZIONE_WIDGET_VERSION} loaded`);

  let lastRecommendation = loadStoredRecommendation();
  let lastImageFailed = false;
  let isRequestInFlight = false;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(() => {
    const root = document.querySelector("[data-luzione-ai-chat]");
    if (!root) {
      console.warn("[Luzione AI] root [data-luzione-ai-chat] not found");
      return;
    }

    const launcher = root.querySelector("[data-luzione-launcher]");
    const panel = root.querySelector("[data-luzione-panel]");
    const closeButton = root.querySelector("[data-luzione-close]");
    const messages = root.querySelector("[data-luzione-messages]");
    const fileInput = root.querySelector("[data-luzione-file]");
    const resetButton = root.querySelector("[data-luzione-reset]");
    const textInput = root.querySelector("[data-luzione-text-input]");
    const sendButton = root.querySelector("[data-luzione-send]");
    const uploadButton = root.querySelector("[data-luzione-upload-btn]");
    const roomSelect = root.querySelector("[data-luzione-room-select]");
    const openLinks = document.querySelectorAll("[data-luzione-open]");

    let hasStarted = false;
    if (textInput && !textInput.placeholder) textInput.placeholder = INPUT_PLACEHOLDER;

    function openPanel() {
      root.classList.add("is-open");
      panel?.setAttribute("aria-hidden", "false");
      if (!hasStarted) startConversation();
      setTimeout(() => textInput?.focus(), 80);
    }

    function closePanel() {
      root.classList.remove("is-open");
      panel?.setAttribute("aria-hidden", "true");
    }

    function startConversation() {
      hasStarted = true;
      lastRecommendation = loadStoredRecommendation();
      lastImageFailed = false;
      if (messages) messages.innerHTML = "";
      addBubble("Hey there — looking for something timeless and refined?", "assistant");
      addBubble(
        "Ask me about Luzione, describe a room, or upload an inspiration image. I’ll help you shape a curated direction.",
        "assistant"
      );
    }

    launcher?.addEventListener("click", openPanel);
    closeButton?.addEventListener("click", closePanel);

    resetButton?.addEventListener("click", async () => {
      lastRecommendation = null;
      lastImageFailed = false;
      clearStoredRecommendation();
      if (roomSelect) roomSelect.value = "Other";
      startConversation();

      try {
        await fetchWithTimeout(API_RESET_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          cache: "no-store",
          body: JSON.stringify({
            session_id: getOrCreateSessionId(),
            shop: SHOP_DOMAIN,
          }),
        }, 8000);
      } catch (error) {
        console.warn("[Luzione AI] reset-memory failed but UI reset continued", error);
      }
    });

    fileInput?.addEventListener("change", async (event) => {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      await handleImageFile(file);
      fileInput.value = "";
    });

    uploadButton?.addEventListener("click", () => fileInput?.click());
    sendButton?.addEventListener("click", handleSend);

    textInput?.addEventListener("keydown", async (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        await handleSend();
      }
    });

    openLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        openPanel();
      });
    });

    panel?.addEventListener("dragenter", (event) => {
      event.preventDefault();
      panel.classList.add("is-dragover");
    });

    panel?.addEventListener("dragover", (event) => {
      event.preventDefault();
      panel.classList.add("is-dragover");
    });

    panel?.addEventListener("dragleave", (event) => {
      if (!panel.contains(event.relatedTarget)) {
        panel.classList.remove("is-dragover");
      }
    });

    panel?.addEventListener("drop", async (event) => {
      event.preventDefault();
      panel.classList.remove("is-dragover");

      const file = Array.from(event.dataTransfer?.files || []).find((item) =>
        item.type && item.type.startsWith("image/")
      );

      if (!file) {
        addBubble("Please drop an image file so I can review the room or inspiration.", "assistant");
        return;
      }

      await handleImageFile(file);
    });
  });

  function getRoot() {
    return document.querySelector("[data-luzione-ai-chat]");
  }

  function getMessages() {
    return document.querySelector("[data-luzione-messages]");
  }

  function getTextInput() {
    return document.querySelector("[data-luzione-text-input]");
  }

  function addBubble(text, role = "assistant", loading = false) {
    const id = `luzione-msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const messages = getMessages();
    if (!messages) return id;

    const bubble = document.createElement("div");
    bubble.id = id;
    bubble.className = `luzione-ai-bubble luzione-ai-bubble--${role}`;
    if (loading) bubble.classList.add("is-loading");
    bubble.textContent = text || "";

    messages.appendChild(bubble);
    scrollToBottom(messages);
    return id;
  }

  function removeBubble(id) {
    const el = document.getElementById(id);
    if (el) el.remove();
  }

  function addImageBubble(file) {
    const messages = getMessages();
    if (!messages) return;

    const url = URL.createObjectURL(file);
    const wrapper = document.createElement("div");
    wrapper.className = "luzione-ai-bubble luzione-ai-bubble--user luzione-ai-image-bubble";

    const img = document.createElement("img");
    img.src = url;
    img.alt = "Uploaded inspiration";
    img.onload = () => URL.revokeObjectURL(url);

    wrapper.appendChild(img);
    messages.appendChild(wrapper);
    scrollToBottom(messages);
  }

  function renderRecommendation(data, options = {}) {
    const normalized = normalizeResponseForDisplay(data, options);
    const message =
      normalized.message ||
      normalized.reply ||
      "I can help with that. Tell me a little more about the room or upload an inspiration image.";

    addBubble(message, "assistant");

    const messages = getMessages();
    if (!messages) return;

    const products = Array.isArray(normalized.products) ? normalized.products.slice(0, 3) : [];

    if (!products.length) {
      renderDesignerCta(normalized.designer_cta);
      if (normalized.follow_up) addBubble(normalized.follow_up, "assistant");
      scrollToBottom(messages);
      return;
    }

    const cards = document.createElement("div");
    cards.className = "luzione-ai-cards";

    products.forEach((product) => {
      const card = document.createElement("article");
      card.className = "luzione-ai-card";

      const imageWrap = document.createElement("div");
      imageWrap.className = "luzione-ai-card__image-wrap";

      const imageUrl = getProductImage(product);
      if (imageUrl) {
        const image = document.createElement("img");
        image.className = "luzione-ai-card__image";
        image.src = imageUrl;
        image.alt = getProductTitle(product);
        image.loading = "lazy";
        image.onerror = () => {
          image.remove();
          imageWrap.classList.add("is-empty");
          imageWrap.appendChild(createImageFallback());
        };
        imageWrap.appendChild(image);
      } else {
        imageWrap.classList.add("is-empty");
        imageWrap.appendChild(createImageFallback());
      }

      const body = document.createElement("div");
      body.className = "luzione-ai-card__body";

      const title = document.createElement("h3");
      title.className = "luzione-ai-card__title";
      title.textContent = getProductTitle(product);

      const reason = document.createElement("p");
      reason.className = "luzione-ai-card__reason";
      reason.textContent = getProductReason(product);

      const actions = document.createElement("div");
      actions.className = "luzione-ai-card__actions";

      const moodboard = document.createElement("button");
      moodboard.type = "button";
      moodboard.className = "luzione-ai-card__button";
      moodboard.textContent = "Moodboard";
      moodboard.addEventListener("click", () => {
        addBubble(`${title.textContent} has been added to your moodboard direction.`, "assistant");
      });

      const link = document.createElement("a");
      link.className = "luzione-ai-card__link";
      link.href = getProductUrl(product);
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = "View";
      if (link.href.endsWith("#")) {
        link.setAttribute("aria-disabled", "true");
        link.addEventListener("click", (event) => event.preventDefault());
      }

      actions.appendChild(moodboard);
      actions.appendChild(link);
      body.appendChild(title);
      body.appendChild(reason);
      body.appendChild(actions);
      card.appendChild(imageWrap);
      card.appendChild(body);
      cards.appendChild(card);
    });

    messages.appendChild(cards);
    renderDesignerCta(normalized.designer_cta);
    if (normalized.follow_up) addBubble(normalized.follow_up, "assistant");
    scrollToBottom(messages);
  }

  function renderDesignerCta(cta) {
    if (!cta || cta.type !== "designer_lead") return;

    const messages = getMessages();
    if (!messages) return;

    const existing = messages.querySelector("[data-luzione-designer-cta]");
    if (existing) existing.remove();

    const wrapper = document.createElement("div");
    wrapper.className = "luzione-ai-designer-cta";
    wrapper.setAttribute("data-luzione-designer-cta", "true");

    const title = document.createElement("strong");
    title.textContent = cta.title || "Interior designer help";

    const message = document.createElement("p");
    message.textContent = cta.message || "Our interior design team can help refine this room in more detail.";

    const button = document.createElement("button");
    button.type = "button";
    button.textContent = cta.button_label || "Open form";
    button.addEventListener("click", () => renderDesignerLeadForm(cta));

    wrapper.appendChild(title);
    wrapper.appendChild(message);
    wrapper.appendChild(button);
    messages.appendChild(wrapper);
    scrollToBottom(messages);
  }

  function renderDesignerLeadForm(cta = {}) {
    const messages = getMessages();
    if (!messages) return;

    const existing = messages.querySelector("[data-luzione-designer-form]");
    if (existing) {
      existing.scrollIntoView({ block: "nearest" });
      return;
    }

    const form = document.createElement("form");
    form.className = "luzione-ai-designer-form";
    form.setAttribute("data-luzione-designer-form", "true");

    const name = createLeadInput("name", "Name", "text", true);
    const email = createLeadInput("email", "Email", "email", true);
    const phone = createLeadInput("phone", "Phone (optional)", "tel", false);
    const note = document.createElement("textarea");
    note.name = "message";
    note.required = true;
    note.rows = 3;
    note.placeholder = "Tell us what you would like help with";
    note.value = buildDefaultLeadMessage();

    const error = document.createElement("p");
    error.className = "luzione-ai-designer-form__error";
    error.setAttribute("aria-live", "polite");

    const actions = document.createElement("div");
    actions.className = "luzione-ai-designer-form__actions";

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.textContent = "Submit";

    actions.appendChild(submit);
    form.appendChild(name);
    form.appendChild(email);
    form.appendChild(phone);
    form.appendChild(note);
    form.appendChild(error);
    form.appendChild(actions);

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      error.textContent = "";

      if (!name.value.trim() || !email.value.trim() || !note.value.trim()) {
        error.textContent = "Please add your name, email, and a short note.";
        return;
      }

      submit.disabled = true;
      submit.textContent = "Sending";

      try {
        const response = await fetchWithTimeout(API_DESIGNER_LEADS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          cache: "no-store",
          body: JSON.stringify({
            name: name.value.trim(),
            email: email.value.trim(),
            phone: phone.value.trim(),
            message: note.value.trim(),
            room_type: getSelectedRoom(),
            session_id: getOrCreateSessionId(),
            shop: SHOP_DOMAIN,
            vision: lastRecommendation?.vision || null,
            product_handles: (lastRecommendation?.products || []).map((product) => product.handle).filter(Boolean),
            language: lastRecommendation?.language || "en",
          }),
        }, 15000);

        const data = await parseJsonResponse(response);
        if (!response.ok || !data?.ok) throw new Error(`Lead failed: ${JSON.stringify(data)}`);

        form.remove();
        addBubble(cta.success_message || "Thank you. Your request has been saved for the design team.", "assistant");
      } catch (requestError) {
        console.error("[Luzione AI] designer lead failed", requestError);
        error.textContent = "Could not submit the form right now. Please try again.";
      } finally {
        submit.disabled = false;
        submit.textContent = "Submit";
      }
    });

    messages.appendChild(form);
    scrollToBottom(messages);
  }

  function createLeadInput(name, placeholder, type, required) {
    const input = document.createElement("input");
    input.name = name;
    input.type = type;
    input.placeholder = placeholder;
    input.required = required;
    return input;
  }

  function buildDefaultLeadMessage() {
    const summary = lastRecommendation?.vision?.scene_summary || "";
    const products = (lastRecommendation?.products || []).map((product) => getProductTitle(product)).filter(Boolean);
    const parts = [];
    if (summary) parts.push(summary);
    if (products.length) parts.push(`Products discussed: ${products.slice(0, 3).join(", ")}`);
    return parts.join("\n");
  }

  function normalizeResponseForDisplay(data, options = {}) {
    const response = data && typeof data === "object" ? { ...data } : { message: String(data || "") };

    if (options.textIntent === "general" && responseLooksLikeForcedProductRecommendation(response)) {
      return buildGeneralConciergeAnswer();
    }

    if (options.allowProducts === false) {
      return {
        ...response,
        products: [],
      };
    }

    response.products = sanitizeProducts(response.products || []);
    return response;
  }

  function buildGeneralConciergeAnswer() {
    return {
      message:
        "Luzione is a luxury design house focused on refined furniture and decor. We help customers discover curated pieces for living rooms, dining spaces, bedrooms, offices, and design-led interiors. You can browse the catalog, ask for styling guidance, or upload a room or inspiration image so I can suggest pieces that fit your space.",
      products: [],
      follow_up:
        "Would you like help exploring a specific room, product category, or design direction?",
    };
  }

  function responseLooksLikeForcedProductRecommendation(response) {
    const products = Array.isArray(response.products) ? response.products : [];
    const text = `${response.message || ""} ${response.reply || ""}`.toLowerCase();

    return (
      products.length > 0 ||
      text.includes("i recommend") ||
      text.includes("my strongest recommendations") ||
      text.includes("matched that direction") ||
      text.includes("selected") ||
      text.includes("these pieces")
    );
  }

  function sanitizeProducts(products) {
    if (!Array.isArray(products)) return [];

    const seen = new Set();

    return products
      .map((product) => {
        if (!product || typeof product !== "object") return null;

        const title = getProductTitle(product);
        const url = getProductUrl(product);
        const image = getProductImage(product);

        if (!title || title === "Luzione piece") return null;
        if (!url || url === "#") return null;

        const key = product.handle || url || title;
        if (seen.has(key)) return null;
        seen.add(key);

        return {
          ...product,
          title,
          url,
          image,
          reason: getProductReason(product),
        };
      })
      .filter(Boolean)
      .slice(0, 3);
  }

  function createImageFallback() {
    const fallback = document.createElement("div");
    fallback.className = "luzione-ai-card__image-fallback";
    fallback.textContent = "LUZIONE";
    return fallback;
  }

  async function handleSend() {
    if (isRequestInFlight) return;

    const input = getTextInput();
    const text = (input?.value || "").trim();
    if (!text) return;

    input.value = "";
    addBubble(text, "user");

    if (lastImageFailed && !lastRecommendation) {
      addBubble(
        "The image did not reach the design analysis service, so I do not have room context yet. Please upload the image again, or describe the room and I’ll continue from there.",
        "assistant"
      );
      return;
    }

    const textIntent = inferTextIntent(text);
    const allowProducts = shouldAllowProductCardsForText(text, textIntent);
    const loadingId = addBubble(
      textIntent === "general" ? "Thinking with the Luzione concierge" : "Thinking with the Luzione catalog",
      "assistant",
      true
    );

    setBusy(true);

    try {
      const response = await fetchWithTimeout(API_CHAT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        body: JSON.stringify({
          message: text,
          intent: textIntent,
          wants_products: allowProducts,
          room_type: getSelectedRoom(),
          session_id: getOrCreateSessionId(),
          shop: SHOP_DOMAIN,
          last_vision: lastRecommendation?.vision || null,
          last_products: lastRecommendation?.products || [],
          has_image_context: Boolean(lastRecommendation?.vision),
        }),
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(`Backend returned ${response.status}: ${JSON.stringify(data)}`);

      removeBubble(loadingId);
      rememberRecommendation(data);
      renderRecommendation(data, {
        textIntent,
        allowProducts,
      });
    } catch (error) {
      console.error("[Luzione AI] chat failed", error);
      removeBubble(loadingId);

      if (textIntent === "general") {
        renderRecommendation(buildGeneralConciergeAnswer(), { textIntent: "general", allowProducts: false });
      } else {
        addBubble(
          "Something went wrong while reviewing your request. Please try again or contact our interior design team.",
          "assistant"
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleImageFile(file) {
    if (isRequestInFlight) return;

    if (!file || !file.type || !file.type.startsWith("image/")) {
      addBubble("Please upload an image file so I can review the room or inspiration.", "assistant");
      return;
    }

    openWidgetFromAnyState();

    const messageInput = getTextInput();
    const userMessage = (messageInput?.value || "").trim();
    if (messageInput) messageInput.value = "";

    addImageBubble(file);
    if (userMessage) addBubble(userMessage, "user");

    const loadingId = addBubble("Reviewing your inspiration image and matching it to the Luzione catalog", "assistant", true);
    setBusy(true);

    try {
      const compressedFile = await compressImageForUpload(file);
      const formData = new FormData();

      formData.append("file", compressedFile, compressedFile.name || file.name || "luzione-upload.jpg");
      formData.append("room_type", getSelectedRoom());
      formData.append("message", userMessage);
      formData.append("user_message", userMessage);
      formData.append("session_id", getOrCreateSessionId());
      formData.append("shop", SHOP_DOMAIN);
      formData.append("client_widget_version", LUZIONE_WIDGET_VERSION);

      const response = await fetchWithTimeout(API_IMAGE_ENDPOINT, {
        method: "POST",
        body: formData,
        cache: "no-store",
      });

      const data = await parseJsonResponse(response);
      if (!response.ok) throw new Error(`Backend returned ${response.status}: ${JSON.stringify(data)}`);

      rememberRecommendation(data);
      lastImageFailed = false;
      removeBubble(loadingId);
      renderRecommendation(data, {
        textIntent: "image_recommendation",
        allowProducts: true,
      });
    } catch (error) {
      console.error("[Luzione AI] image recommendation failed", error);
      lastImageFailed = true;
      removeBubble(loadingId);

      if (error?.name === "AbortError") {
        addBubble(
          "The image analysis is taking longer than expected. Please try once more with the same image, or describe the room and I’ll continue from there.",
          "assistant"
        );
      } else {
        addBubble(
          "Something went wrong while reviewing the image. Please try another image or contact our interior design team.",
          "assistant"
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function compressImageForUpload(file) {
    const maxEdge = 768;
    const quality = 0.68;

    if (!file.type || !file.type.startsWith("image/")) return file;

    try {
      const image = await loadImageElement(file);
      const scale = Math.min(1, maxEdge / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
      const width = Math.max(1, Math.round((image.naturalWidth || image.width) * scale));
      const height = Math.max(1, Math.round((image.naturalHeight || image.height) * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.drawImage(image, 0, 0, width, height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (!blob) return file;

      const originalBase = (file.name || "luzione-upload").replace(/\.(png|webp|jpeg|jpg|heic|heif)$/i, "");
      return new File([blob], `${originalBase}-luzione.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch (error) {
      console.warn("[Luzione AI] image compression failed; using original file", error);
      return file;
    }
  }

  function loadImageElement(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not load image for compression"));
      };

      img.src = url;
    });
  }

  async function fetchWithTimeout(url, options = {}, timeoutMs = FETCH_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }
  }

  async function parseJsonResponse(response) {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) return response.json();
    return { message: await response.text() };
  }

  function inferTextIntent(text) {
    const value = normalizeText(text);

    if (isGeneralInfoIntent(value) && !isProductSeekingIntent(value)) {
      return "general";
    }

    if (isProductSeekingIntent(value) || isRoomFollowUpIntent(value)) {
      return "recommendation";
    }

    return "general";
  }

  function shouldAllowProductCardsForText(text, textIntent) {
    const value = normalizeText(text);
    if (textIntent !== "recommendation") return false;
    if (isGeneralInfoIntent(value) && !isProductSeekingIntent(value)) return false;
    return true;
  }

  function normalizeText(text) {
    return String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[ıİ]/g, "i");
  }

  function isGeneralInfoIntent(value) {
    return /\b(luzione|marka|brand|company|sirket|hizmet|service|services|ne yapiyorsunuz|neler yapiyorsunuz|what do you do|who are you|tanit|tanitir|about|hakkinda|support|contact|iletisim|shipping|delivery|return|refund|payment|odeme)\b/i.test(value);
  }

  function isProductSeekingIntent(value) {
    return /\b(recommend|recommendation|suggest|suggestion|oneri|oner|onerir|urun|product|piece|furniture|mobilya|sofa|koltuk|armchair|chair|sandalye|bed|yatak|table|masa|lamp|lamba|bench|sehpa|dining|living room|bedroom|office|bu oda|this room|fit|match|style|design direction|moodboard)\b/i.test(value);
  }

  function isRoomFollowUpIntent(value) {
    return /\b(this|that|it|room|space|image|photo|picture|bunu|buna|buraya|oda|resim|fotograf|gorsel|daha|another|else|alternative|alternatif)\b/i.test(value);
  }

  function getSelectedRoom() {
    const select = document.querySelector("[data-luzione-room-select]");
    return select?.value || "Other";
  }

  function rememberRecommendation(data) {
    if (!data || typeof data !== "object") return;
    const products = sanitizeProducts(data.products || []);
    const sceneType = String(data.vision?.scene_type || data.vision?.image_type || "");
    const hasImageContext = Boolean(data.has_image_context || (data.vision && sceneType !== "text_request"));
    const hasProducts = products.length > 0;
    if (!hasImageContext && !hasProducts) return;

    lastRecommendation = {
      vision: hasImageContext ? data.vision || lastRecommendation?.vision || null : lastRecommendation?.vision || null,
      products: hasProducts ? products : sanitizeProducts(lastRecommendation?.products || []),
      language: data.language || lastRecommendation?.language || "en",
      session_id: data.session_id || getOrCreateSessionId(),
    };
    if (!lastRecommendation.vision && !lastRecommendation.products.length) return;
    saveStoredRecommendation(lastRecommendation);
  }

  function loadStoredRecommendation() {
    try {
      const raw = window.localStorage.getItem("luzione_ai_last_recommendation");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return {
        vision: parsed.vision || null,
        products: sanitizeProducts(parsed.products || []),
        language: parsed.language || "en",
        session_id: parsed.session_id || "",
      };
    } catch (_) {
      return null;
    }
  }

  function saveStoredRecommendation(value) {
    try {
      window.localStorage.setItem("luzione_ai_last_recommendation", JSON.stringify(value || null));
    } catch (_) {
      // Ignore storage failures in private browsing or blocked storage contexts.
    }
  }

  function clearStoredRecommendation() {
    try {
      window.localStorage.removeItem("luzione_ai_last_recommendation");
    } catch (_) {
      // Ignore storage failures in private browsing or blocked storage contexts.
    }
  }

  function getOrCreateSessionId() {
    const key = "luzione_ai_session_id";
    let sessionId = window.localStorage.getItem(key);

    if (!sessionId) {
      sessionId = `luzione-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.localStorage.setItem(key, sessionId);
    }

    return sessionId;
  }

  function openWidgetFromAnyState() {
    const root = getRoot();
    const panel = document.querySelector("[data-luzione-panel]");
    root?.classList.add("is-open");
    panel?.setAttribute("aria-hidden", "false");
  }

  function setBusy(busy) {
    isRequestInFlight = busy;

    const sendButton = document.querySelector("[data-luzione-send]");
    const uploadButton = document.querySelector("[data-luzione-upload-btn]");
    const textInput = getTextInput();

    if (sendButton) sendButton.disabled = busy;
    if (uploadButton) uploadButton.disabled = busy;
    if (textInput) textInput.disabled = busy;
  }

  function scrollToBottom(messages) {
    requestAnimationFrame(() => {
      messages.scrollTop = messages.scrollHeight;
    });
  }

  function getProductTitle(product) {
    return String(product?.title || product?.name || "Luzione piece").trim();
  }

  function getProductReason(product) {
    return String(
      product?.reason ||
        product?.insight ||
        product?.description ||
        product?.body_html ||
        "Selected as part of this refined Luzione direction."
    )
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 220);
  }

  function getProductUrl(product) {
    const direct =
      product?.url ||
      product?.product_url ||
      product?.link ||
      product?.onlineStoreUrl ||
      product?.online_store_url ||
      "";

    const normalizedDirect = normalizeUrl(direct);
    if (isValidCustomerProductUrl(normalizedDirect)) return normalizedDirect;

    const handle = String(product?.handle || product?.slug || "").trim().replace(/^\/+|\/+$/g, "");
    if (handle) return `${PRODUCT_BASE_URL}${encodeURIComponent(handle)}`;

    return "#";
  }

  function getProductImage(product) {
    const candidates = [
      product?.image,
      product?.image_url,
      product?.imageUrl,
      product?.featured_image,
      product?.featuredImage,
      product?.featured_media,
      product?.featuredMedia,
      product?.media?.[0],
      product?.images?.[0],
      product?.variants?.[0]?.image,
      product?.variant_image,
      product?.variantImage,
    ];

    for (const candidate of candidates) {
      const url = extractImageUrl(candidate);
      if (isValidCustomerImageUrl(url)) return url;
    }

    return "";
  }

  function extractImageUrl(value) {
    if (!value) return "";

    if (typeof value === "string") {
      return normalizeUrl(value);
    }

    if (typeof value === "object") {
      return normalizeUrl(
        value.src ||
          value.url ||
          value.image_url ||
          value.imageUrl ||
          value.preview_image?.src ||
          value.previewImage?.url ||
          value.originalSrc ||
          value.transformedSrc ||
          ""
      );
    }

    return "";
  }

  function normalizeUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.startsWith("//")) return `https:${raw}`;
    return raw;
  }

  function isValidCustomerImageUrl(url) {
    if (!isHttpUrl(url)) return false;
    if (/catalog-images\/(train|test|val)/i.test(url)) return false;
    if (/\/(train|test|val)\/images\//i.test(url)) return false;
    if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(url)) return false;
    return true;
  }

  function isValidCustomerProductUrl(url) {
    if (!isHttpUrl(url)) return false;
    if (/localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(url)) return false;
    if (!/luzione\.com\/products\//i.test(url)) return false;
    return true;
  }

  function isHttpUrl(url) {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch (_) {
      return false;
    }
  }
})();
